import { IHandleQuickSearchResult, ISearchProviderConfig } from '../../../types';
import mapArticle from '../helpers/mappers/mapArticle';
import mapCollection from '../helpers/mappers/mapCollection';
import mapPage from '../helpers/mappers/mapPage';
import mapPredSearchProduct from '../helpers/mappers/mapPredSearchProduct';
import mapSuggestion from '../helpers/mappers/mapSuggestion';
import { elevate, localStorageBackedSession } from '@apptus/esales-api';
import {
  getClusterId,
  getMarket,
  getLocale,
  getTouchpoint,
  getViewId,
  getVoyadoSession,
  hasCookieConsent
} from '../helpers/voyadoHelpers';
import { elevateFetchShim } from '../helpers/elevateFetchShim';
import fetchContextualPrices from '../helpers/fetchContextualPrices';

// Simple in-memory cache for page reachability checks
const pageReachabilityCache = new Map<string, boolean>();

const HandleSearch = async (
  searchTerm: string,
  config: ISearchProviderConfig
): Promise<IHandleQuickSearchResult> => {
  const mappedResponse: IHandleQuickSearchResult = {
    raw: null,
    typos: [],
    products: [],
    collections: [],
    suggestions: [],
    pages: [],
    articles: [],
    popularSearches: [],
    trendingProducts: []
  };

  try {
    // Step 1: Ensure DOM is ready and config element exists
    const configElement = document.querySelector('[data-app-layer-config-search]');
    if (!configElement) {
      console.warn('Config DOM element not found, waiting for DOM...');
      // Wait a bit for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Step 2: Validate all required config values are available
    const market = getMarket();
    const locale = getLocale();
    const touchpoint = getTouchpoint();
    const clusterId = config.clusterId || getClusterId();

    // Step 2: Ensure session is properly initialized and keys are generated
    // Use ephemeral session (random per request) if cookies declined, otherwise persisted session
    const session = getVoyadoSession(localStorageBackedSession);
    const sessionKeys = session();
    const hasConsent = hasCookieConsent();

    // Step 3: Validate all required values exist before creating API instance
    if (!clusterId) {
      throw new Error('Cluster ID is missing from config');
    }
    if (!market) {
      throw new Error('Market is missing');
    }
    if (!locale) {
      throw new Error('Locale is missing');
    }
    if (!sessionKeys.customerKey || !sessionKeys.sessionKey) {
      throw new Error('Session keys are not properly initialized');
    }

    // Step 4: Create API instance with validated config
    const api = elevate({
      clusterId,
      market,
      locale,
      touchpoint,
      session: session
    });
    elevateFetchShim();

    // Add small delay to help with potential race conditions
    await new Promise(resolve => setTimeout(resolve, 50));

    // Handle empty search term for pre-search suggestions (and optional trending/popular)
    if (!searchTerm || searchTerm.trim() === '') {
      const autocompleteParams: any = {
        viewId: getViewId(),
        q: '' // Empty query returns recentSearches and recentlyViewed
      };

      // Only include notify when cookies are declined (notify: false)
      if (!hasConsent) {
        autocompleteParams.notify = false;
      }

      const res = await api.query.autocomplete(autocompleteParams);

      console.log('🔥 HandleSearch empty search - Full API response:', res);
      console.log('🔥 HandleSearch empty search - recentlyViewed:', res.recentlyViewed);
      console.log('🔥 HandleSearch empty search - phraseSuggestions:', res.phraseSuggestions);
      console.log('🔥 HandleSearch empty search - recentSearches:', res.recentSearches);
      console.log('🔥 HandleSearch empty search - session keys:', sessionKeys);
      console.log('🔥 HandleSearch empty search - hasConsent:', hasConsent);
      console.log('🔥 HandleSearch empty search - autocompleteParams:', autocompleteParams);

      // Feed recent searches into suggestions so existing UI renders them
      mappedResponse.suggestions = res.phraseSuggestions?.map(s => ({ value: s.q, query: s.q })) || [];

      // Fetch contextual prices for recently viewed products
      const productKeys = res.recentlyViewed?.map(group => group.products[0].key) || [];
      let contextualPrices = {};

      const sfApiConfigStr = document.querySelector('[data-storefront-api-config]')?.textContent;
      let shouldFetchContextual = false;

      if (sfApiConfigStr) {
        try {
          const parsedConfig = JSON.parse(sfApiConfigStr);
          if (parsedConfig.country && parsedConfig.country !== 'GB') {
            shouldFetchContextual = true;
          }
        } catch (e) {
          // failed to parse config
        }
      }

      if (shouldFetchContextual && productKeys.length > 0) {
        contextualPrices = await fetchContextualPrices(productKeys);
      }

      // Map recently viewed products to trending products
      mappedResponse.trendingProducts =
        res.recentlyViewed?.map(group => {
          const contextualData = contextualPrices[group.products[0].key];
          let price = group.products[0]?.sellingPrice?.min;

          // Use contextual price if available
          if (contextualData?.priceRange) {
            price = parseFloat(contextualData.priceRange.minVariantPrice.amount);
          } else if (contextualData?.price) {
            price = parseFloat(contextualData.price.amount);
          }

          return {
            key: group.key,
            title: group.products[0]?.title || '',
            link: group.products[0]?.link || '',
            ticket: group.products[0]?.ticket || '',
            image: group.products[0]?.imageInfo?.images[0]?.sources[0]?.url,
            price: price
          };
        }) || [];

      // Also surface trending products in the products lane for quick search UI
      mappedResponse.products =
        res.recentlyViewed?.flatMap(group =>
          mapPredSearchProduct(group.products[0], '', contextualPrices[group.products[0].key])
        ) || [];

      console.log('🔥 HandleSearch empty search - mappedResponse.trendingProducts:', mappedResponse.trendingProducts);
      console.log('🔥 HandleSearch empty search - mappedResponse.products:', mappedResponse.products);

      return mappedResponse;
    }

    // Try with content lists first, fall back to basic autocomplete if it fails
    const autocompleteParams: any = {
      viewId: getViewId(),
      q: searchTerm
    };
    // Only include notify when cookies are declined (notify: false)
    if (!hasConsent) {
      autocompleteParams.notify = false;
    }
    const res = await api.query.autocomplete(autocompleteParams, {
      contentLists: [{ id: 'content-hits', limit: 4 }]});

    console.log('🔥 HandleSearch res:', res);
    console.log('🔥 HandleSearch contentLists:', res.contentLists);
    console.log('🔥 HandleSearch productSuggestions:', res.productSuggestions);
    console.log('🔥 HandleSearch phraseSuggestions:', res.phraseSuggestions);

    const { contentLists, productSuggestions, phraseSuggestions } = res;

    // Depending on how many contentLists you reference in the query it can return more than one
    // We want to flatten these and only get the items
    const contentListResults = contentLists?.flatMap(item => item.items);
    // Each item has a different type, which we want to split so they're returned separately
    const contentListArticles = contentListResults?.filter(item => item.type === 'article');
    const contentListCollections = contentListResults?.filter(item => item.type === 'category');
    const contentListPages = contentListResults?.filter(item => item.type === 'page');

    // Map the results (filter out nulls from unpublished articles)
    mappedResponse.articles = contentListArticles?.map(article => mapArticle(article, searchTerm)).filter(Boolean);
    mappedResponse.collections = contentListCollections?.map(collection => mapCollection(collection, searchTerm));
    mappedResponse.pages = contentListPages?.map(page => mapPage(page, searchTerm));
    mappedResponse.suggestions = phraseSuggestions?.map(query => mapSuggestion(query));

    // Fetch contextual prices if not on GB site
    const productKeys = productSuggestions?.flatMap(group => group.products[0].key) || [];
    let contextualPrices = {};

    const sfApiConfigStr = document.querySelector('[data-storefront-api-config]')?.textContent;
    let shouldFetchContextual = false;

    if (sfApiConfigStr) {
      try {
        const parsedConfig = JSON.parse(sfApiConfigStr);
        if (parsedConfig.country && parsedConfig.country !== 'GB') {
          shouldFetchContextual = true;
        }
      } catch (e) {
        // failed to parse config
      }
    }

    if (shouldFetchContextual && productKeys.length > 0) {
      contextualPrices = await fetchContextualPrices(productKeys);
    }

    // Voyado returns an array of products within each productSuggestion, we only want the first
    mappedResponse.products = productSuggestions?.flatMap(group =>
      mapPredSearchProduct(group.products[0], searchTerm, contextualPrices[group.products[0].key])
    );

    // Filter out pages that are not reachable (draft/unpublished)
    const isUrlReachable = async (url: string): Promise<boolean> => {
      try {
        if (pageReachabilityCache.has(url)) {
          return pageReachabilityCache.get(url);
        }
      } catch {
        // ignore cache read errors
      }

      let ok = false;
      try {
        const headRes = await fetch(url, { method: 'HEAD', cache: 'no-store' });
        ok = headRes.ok;
      } catch {
        // ignore and try GET
      }
      if (!ok) {
        try {
          const getRes = await fetch(url, { method: 'GET', cache: 'no-store' });
          ok = getRes.ok;
        } catch {
          ok = false;
        }
      }

      try {
        pageReachabilityCache.set(url, ok);
      } catch {
        // ignore cache write errors
      }
      return ok;
    };

    if (Array.isArray(mappedResponse.pages) && mappedResponse.pages.length) {
      const checks = await Promise.all(mappedResponse.pages.map(p => isUrlReachable(p.url)));
      mappedResponse.pages = mappedResponse.pages.filter((_, i) => checks[i]);
    }

    // Filter out articles that are not reachable (draft/unpublished)
    if (Array.isArray(mappedResponse.articles) && mappedResponse.articles.length) {
      const checks = await Promise.all(mappedResponse.articles.map(a => isUrlReachable(a.url)));
      mappedResponse.articles = mappedResponse.articles.filter((_, i) => checks[i]);
    }

    return mappedResponse;
  } catch (e) {
    throw new Error(`Error when getting Quick Search results: ${e}`);
  }
};

export default HandleSearch;